import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getCookie } from "../../utility/getCookie";
import {
  CreateStoreReviewPayload,
  MySellerReviewResponse,
  MySubmittedReviewsResponse,
  SellerFeedbackSummaryResponse,
  SellerReviewsResponse,
  StoreReviewState,
  UpdateStoreReviewPayload,
} from "./types";

const API_URL = [
  process.env.REACT_APP_API_BASE_URL,
  process.env.REACT_APP_SELLER_APP_BACKEND_PREFIX,
]
  .filter(Boolean)
  .join("");

if (!process.env.REACT_APP_API_BASE_URL) {
  console.warn("Warning: REACT_APP_API_BASE_URL is not defined.");
}
if (!process.env.REACT_APP_SELLER_APP_BACKEND_PREFIX) {
  console.warn("Warning: REACT_APP_SELLER_APP_BACKEND_PREFIX is not defined.");
}

// Axios instance with cookies support
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add Bearer token from cookies
api.interceptors.request.use(
  async (config) => {
    let token = getCookie("access_token");

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);
// Async thunk for creating store review
export const createStoreReview = createAsyncThunk(
  "storeReview/create",
  async (payload: CreateStoreReviewPayload, { rejectWithValue }) => {
    try {
      const body: Record<string, any> = {
        targetSellerId: payload.targetSellerId,
        orderId: payload.orderId,
        rating: payload.rating,
      };
      if (payload.review) body.review = payload.review;
      if (payload.reasons && payload.reasons.length > 0) {
        body.reasons = payload.reasons;
      }

      const response = await api.post("/reviews/customer-to-seller", body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create store review",
      );
    }
  },
);

// Async thunk for updating store review
export const updateStoreReview = createAsyncThunk(
  "storeReview/update",
  async (payload: UpdateStoreReviewPayload, { rejectWithValue }) => {
    try {
      const { id, ...data } = payload;
      const response = await api.put(`/reviews/customer-to-seller/${id}`, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update store review",
      );
    }
  },
);

// Async thunk for deleting store review
export const deleteStoreReview = createAsyncThunk(
  "storeReview/delete",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/reviews/customer-to-seller/${id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete store review",
      );
    }
  },
);

// Async thunk for getting submitted reviews
export const getMySubmittedReviews = createAsyncThunk(
  "storeReview/getMySubmittedReviews",
  async (params: { page?: number; limit?: number; search?: string; startDate?: string; endDate?: string }, { rejectWithValue }) => {
    try {
      const response = await api.get<MySubmittedReviewsResponse>("/reviews/customer-to-seller/my-submitted-reviews", {
        params,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch submitted reviews",
      );
    }
  },
);

// Async thunk for getting a seller's public reviews + stats
export const getSellerReviews = createAsyncThunk(
  "storeReview/getSellerReviews",
  async (
    params: { sellerId: string; page?: number; limit?: number; search?: string; filter?: string },
    { rejectWithValue },
  ) => {
    try {
      const { sellerId, ...query } = params;
      const response = await api.get<SellerReviewsResponse>(
        `/reviews/customer-to-seller/${sellerId}`,
        { params: query },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch seller reviews",
      );
    }
  },
);

// Async thunk for getting a seller's per-period feedback summary
export const getSellerFeedbackSummary = createAsyncThunk(
  "storeReview/getSellerFeedbackSummary",
  async (sellerId: string, { rejectWithValue }) => {
    try {
      const response = await api.get<SellerFeedbackSummaryResponse>(
        `/reviews/customer-to-seller/${sellerId}/summary`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch seller feedback summary",
      );
    }
  },
);

// Async thunk for checking the logged-in customer's existing review for a seller+order
export const checkMySellerReview = createAsyncThunk(
  "storeReview/checkMySellerReview",
  async (params: { sellerId: string; orderId?: string }, { rejectWithValue }) => {
    try {
      const response = await api.get<MySellerReviewResponse>(
        `/reviews/customer-to-seller/my-review/${params.sellerId}`,
        { params: params.orderId ? { orderId: params.orderId } : {} },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to check existing review",
      );
    }
  },
);

const initialState: StoreReviewState = {
  loading: false,
  success: false,
  error: null,
  reviewData: null,
  myReviews: [],
  meta: null,
  sellerReviews: [],
  sellerStats: null,
  sellerReviewsMeta: null,
  sellerSummary: null,
  mySellerReview: null,
};

const storeReviewSlice = createSlice({
  name: "storeReview",
  initialState,
  reducers: {
    resetStoreReviewState: (state) => {
      state.loading = false;
      state.success = false;
      state.error = null;
      state.reviewData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createStoreReview.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createStoreReview.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.reviewData = action.payload.data;
      })
      .addCase(createStoreReview.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload as string;
      })
      .addCase(updateStoreReview.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(updateStoreReview.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.reviewData = action.payload.data;
      })
      .addCase(updateStoreReview.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload as string;
      })
      .addCase(deleteStoreReview.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(deleteStoreReview.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
        // Optionally clear the reviewData if deleted
        // state.reviewData = null;
      })
      .addCase(deleteStoreReview.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload as string;
      })
      .addCase(getMySubmittedReviews.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(getMySubmittedReviews.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.myReviews = action.payload.data.reviews;
        state.meta = action.payload.data.meta;
      })
      .addCase(getMySubmittedReviews.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload as string;
      })
      .addCase(getSellerReviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSellerReviews.fulfilled, (state, action) => {
        state.loading = false;
        state.sellerReviews = action.payload.data.reviews;
        state.sellerStats = {
          averageRating: action.payload.data.averageRating,
          totalCount: action.payload.data.totalCount,
          ratingDistribution: action.payload.data.ratingDistribution,
        };
        state.sellerReviewsMeta = action.payload.data.meta;
      })
      .addCase(getSellerReviews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getSellerFeedbackSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSellerFeedbackSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.sellerSummary = action.payload.data;
      })
      .addCase(getSellerFeedbackSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(checkMySellerReview.fulfilled, (state, action) => {
        state.mySellerReview = action.payload.data;
      });
  },
});

export const { resetStoreReviewState } = storeReviewSlice.actions;

export default storeReviewSlice.reducer;
